import { CommonModule } from "@angular/common";
import { Component, EventEmitter, inject, Output } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatExpansionModule } from "@angular/material/expansion";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { isArray } from "lodash";
import {
  catchError,
  EMPTY,
  Subject,
  take,
  takeUntil,
} from "rxjs";
import { ProjectService } from "../../../core/services/project/project.service";
import { RestService } from "../../../core/services/rest.service";
import { TdgService } from "../../../core/services/tdg.service";
import { ToasterService } from "../../../core/services/toaster-service";
import { MessageEnum } from "../../../core/utils/message.enum";
import { ZorroTableComponent } from "../../common/custom-table/zorro-table.component";
import { LoadingComponent } from "../../common/loading/loading.component";
import { NoDataComponent } from "../../common/no-data/no-data.component";
import { SimplePagerComponent } from "../../common/simple-pager/simple-pager.component";
import { DeleteConfirmationComponent } from "../../modal-templates/delete-confirmation/delete-confirmation.component";
import { ValidationWarningModalComponent } from "../../modal-templates/validation-warning-modal/validation-warning-modal.component";

@Component({
  selector: "app-tdg-data-table",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatExpansionModule,
    ZorroTableComponent,
    LoadingComponent,
    NoDataComponent,
    SimplePagerComponent,
  ],
  templateUrl: "./tdg-data-table.component.html",
  styleUrl: "./tdg-data-table.component.scss",
})
export class TdgDataTableComponent {
  @Output() moveToNextTabEvent = new EventEmitter<void>();
  @Output() disableTabsEvent = new EventEmitter<number[]>();
  @Output() backToVersionHistory = new EventEmitter<void>();

  selectedSchema: any = [];
  modalService = inject(NgbModal);

  schemas: any = [];
  isExpanded = true;
  projectId = "";
  isLoading = false;
  userInput = "";
  messages: any = []; // Current test case messages
  stopPolling$ = new Subject<void>();
  destroy$ = new Subject<void>();
  placeholderText =
    "Tell me what you would like to generate as Test Data, aligned to the date tables and attributes shown in the Preview. You may also request for new attributes for given tables...";
  selectedGTestCases: any;
  selectedTestSuiteData: any;
  parseFileId: any;
  tableToDelete:any;
  automationProjectId:string="";
  taskId:string = "";
  errorData:any;
  errorText:string = "";
  isFileValidation:boolean = false;
  showAiSupport = false;
  readonly pageSize = 5;
  currentPage = 0;
  /** Stable page slice. A getter that returns slice() every CD cycle makes
   *  zorro-table see a new array, rewrite every row, and lock the tab. */
  pagedRows: any[] = [];
  readonly tableOptions = { showPagination: false, pageSize: 5 };
  

  constructor(
    private readonly tdgService: TdgService,
    private readonly restService: RestService,
    private readonly projectService: ProjectService,
    public toaster: ToasterService
  ) {}

  ngOnInit() {
    this.projectId = this.tdgService.getProjectId();
    this.projectService.getAutomationProjectId().subscribe((id: any) => {
      this.automationProjectId = id;
    });
    let storedData = this.tdgService.getRetrievedSchemas();
    if (storedData.length) {
      this.schemas = storedData;
      // Ensure each schema has a messages array
      this.schemas.forEach((schema: any) => {
        if (!schema.messages) {
          schema.messages = [];
        }
      });
      this.switchTab(storedData[0]);
    } else {
      this.getTablesForSelectedSchemas();
    }
  }

  onBackToVersionHistory(): void {
    this.backToVersionHistory.emit();
  }

  openAiSupport(): void {
    this.showAiSupport = true;
  }

  closeAiSupport(): void {
    this.showAiSupport = false;
  }

  getTablesForSelectedSchemas() {
    this.isLoading = true;

    this.tdgService.getSelectedTestSuite().pipe(take(1)).subscribe((data: any) => {
      this.selectedTestSuiteData = data;

      const selectedFile = this.tdgService.getSelectedFile();
      const fileOrConnectionId =
        selectedFile?.file?.id ||
        selectedFile?.file?._id ||
        selectedFile?.file?.connectionId ||
        this.tdgService.getSelectedFileName();

      const payload = {
        filenames: fileOrConnectionId ? [fileOrConnectionId] : [],
        project_id: this.tdgService.getProjectId(),
        testSuite: data?.testSuite,
        testSuiteData: {
          testCases: [...(data?.testCases ?? [])],
        },
        dataSource: data?.dataSource,
      };

      this.restService.parseSqlTDG(payload).subscribe(
        (response: any) => {
          if (response?.status === "pending" && response?.task_id) {
            this.taskId = response.task_id;
            this.checkStatus();
          }
        },
        (error) => {
          console.error(error);
          this.isLoading = false;
        }
      );
    });
  }

  checkStatus() {
    this.isLoading = true;
    this.restService
      .pollTdgTaskStatus(this.tdgService.getProjectId(), this.taskId, 10000)
      .pipe(
        takeUntil(this.destroy$),
        catchError((error) => this.handlePollingError(error))
      )
      .subscribe((res: any) => this.handleStatusResponse(res));
  }

  private handlePollingError(error: any) {
    console.error("An error occurred while loading recorded activities:", error);
    this.isLoading = false;
    return EMPTY;
  }

  private handleStatusResponse(res: any): void {
    if (res.status === 'failed') {
      this.stopPolling$.next();
      return;
    }
    
    if (res.status === 'completed') {
      this.stopPolling$.next();
      this.processCompletedStatus(res);
    }
  }

  private processCompletedStatus(res: any): void {
    if (res.result?.combined_tables?.length > 0) {
      this.processCombinedTables(res.result.combined_tables, res.result.db_name);
    }
    
    if (res.result?.validation_errors?.length > 0) {
      this.processValidationErrors(res.result.validation_errors);
    }
    
    this.parseFileId = res.result?.parseFileId;
    this.isLoading = false;
  }

  private processCombinedTables(combinedTables: any[], dbName: string): void {
    const formattedSchemas = combinedTables.map((table: any) => 
      this.formatTableSchema(table, dbName)
    );
    
    this.schemas = this.tdgService.getFormatedResponse(formattedSchemas);
    this.tdgService.setRetrievedSchemas(this.schemas);
    this.switchTab(this.schemas[0]);
  }

  private formatTableSchema(table: any, dbName: string): any {
    const formattedRawData = Array.isArray(table.rawData) ? table.rawData : [];

    const connectionId = this.findConnectionId(dbName);
    const piiColumns = table.piiColumns ?? table.pii_columns ?? [];

    return {
      db_name: table.testCaseId,
      testCaseId: table.testCaseId,
      columns: table.columns,
      piiColumns,
      pii_columns: piiColumns,
      messages: [],
      connectionId: connectionId,
      tables: [
        {
          tableName: table.testCaseId,
          columns: table.columns,
          rawData: formattedRawData,
          piiColumns,
          pii_columns: piiColumns,
        },
      ],
    };
  }

  private findConnectionId(dbName: string): string | undefined {
    const selectedSchemas = this.tdgService.getSelectedSchemas();
    return selectedSchemas.find(
      (ele: any) => ele.connectionName.replaceAll(".sql", "") === dbName
    )?.id;
  }

  private processValidationErrors(validationErrors: any[]): void {
    const isFileValidation = validationErrors[0]?.type === 'FILE_VALIDATION';
    
    this.isFileValidation = isFileValidation;
    this.errorData = this.filterErrorsByType(validationErrors, isFileValidation);
    this.errorText = this.buildErrorText(isFileValidation);
    
    this.openValidationError();
  }

  private filterErrorsByType(errors: any[], isFileValidation: boolean): any[] {
    const filterType = isFileValidation ? 'FILE_VALIDATION' : 'COMPATIBILITY';
    return errors.filter((item: any) => item.type === filterType);
  }

  private buildErrorText(isFileValidation: boolean): string {
    const count = this.errorData.length;
    return isFileValidation 
      ? `${count} file(s) failed file validation.`
      : `${count} test case(s) failed compatibility validation.`;
  }

  expand() {
    this.isExpanded = !this.isExpanded;
  }

  addDataSchema() {}

  switchTab(schema: any) {
    this.selectedSchema = schema;
    this.messages = this.selectedSchema.messages || [];
    this.currentPage = 0;
    this.updatePagedRows();
  }

  private updatePagedRows(): void {
    const start = this.currentPage * this.pageSize;
    this.pagedRows = this.tableRows.slice(start, start + this.pageSize);
  }

  get tableRows(): any[] {
    return this.selectedSchema?.tables?.[0]?.rawData ?? [];
  }

  get pagerStart(): number {
    return this.tableRows.length === 0 ? 0 : this.currentPage * this.pageSize + 1;
  }

  get pagerEnd(): number {
    return Math.min((this.currentPage + 1) * this.pageSize, this.tableRows.length);
  }

  get disablePrevPage(): boolean {
    return this.currentPage === 0;
  }

  get disableNextPage(): boolean {
    return (this.currentPage + 1) * this.pageSize >= this.tableRows.length;
  }

  prevPage(): void {
    if (!this.disablePrevPage) {
      this.currentPage--;
      this.updatePagedRows();
    }
  }

  nextPage(): void {
    if (!this.disableNextPage) {
      this.currentPage++;
      this.updatePagedRows();
    }
  }

  getPrimaryKey(schema: any) {
    schema.tables?.forEach((table: any) => {
      table.columns = table?.columns?.map((col: any) => {
        const isPrimaryKey = table?.primaryKey === col.key;
        const isForeignKey = table?.foreignKey?.find(
          (fk: any) => fk.column === col.key
        )?.table;

        let displayPrefix = "";
        if (isPrimaryKey) {
          displayPrefix = " 🔑";
        } else if (isForeignKey) {
          displayPrefix = " 🔗";
        }

        return {
          display_name: `${
            col.key?.charAt(0) + col.key?.slice(1).replace(/_/g, " ")
          }${displayPrefix}`,
          key: col.key,
          headerStyles: {
            background: "#5e1de4",
            color: "#ffffff",
            padding: "4px 8px",
            width: "100px",
          },
        };
      });
    });

    return schema;
  }
  deleteSchema(index: number) {
    this.schemas.splice(index, 1);
  }

  compose() {
    if (!this.userInput) {
      return;
    }
    let fileName = this.tdgService.getSelectedFileName();
    this.messages.push({ text: this.userInput, isSent: true });
    // Update the schema's messages
    this.selectedSchema.messages = [...this.messages];
    let input = this.userInput;
    this.userInput = "";
    this.restService
      .sendPromptTDG(
        this.tdgService.getProjectId(),
        fileName,
        input,
        this.tdgService.getSessionId(),
        this.parseFileId,
        this.selectedSchema.testCaseId
      )
      .subscribe(
        (response: any) => {
          if (response.task_id) {
            this.messages.push({ text: response.message });
            this.selectedSchema.messages = [...this.messages];
            this.updateSchemasInStore();
          }
            this.tdgService.setTaskId(response.task_id);
            this.loadData();
        },
        (error) => {
          console.error(error);
        }
      );
  }

  loadData() {
    this.isLoading = true;
    this.restService
      .pollTdgTaskStatus(
        this.tdgService.getProjectId(),
        this.tdgService.getTaskId(),
        5000
      )
      .pipe(
        takeUntil(this.destroy$),
        catchError((error) => {
          console.error(
            "An error occurred while loading recorded activities:",
            error
          );
          this.isLoading = false;
          return EMPTY;
        })
      )
      .subscribe((res: any) => {
        if (res.status === "failed") {
          this.isLoading = false;
          this.messages.push({ text: res.error, isWarning: true });
          this.selectedSchema.messages = [...this.messages];
          this.updateSchemasInStore();
          this.stopPolling$.next();
        } else if (res.status === "completed") {
          this.stopPolling$.next();
          this.tdgService.setSessionId(res.session_id);
          this.isLoading = false;
          if (!isArray(res.result) && res.result?.warning) {
            this.messages.push({ text: res.result.warning, isWarning: true });
            this.selectedSchema.messages = [...this.messages];
            this.updateSchemasInStore();
            return;
          }
          this.messages.push({ text: "Test data generated successfully!" });
          this.selectedSchema.messages = [...this.messages];
          this.updateSchemasInStore();
          
          // Handle new response structure with test_cases
          if (res.result?.test_cases && res.result.test_cases.length > 0) {
            const testCases = res.result.test_cases;
            
            // Find the index of current schema
            let ind = this.schemas.findIndex(
              (ele: any) => ele.testCaseId === this.selectedSchema.testCaseId
            );
            
            if (ind !== -1) {
              // Format the test case data for the current schema
              const currentTestCase = testCases.find(
                (tc: any) => tc.testCaseId === this.selectedSchema.testCaseId
              );
              
              if (currentTestCase) {
                const formattedRawData = Array.isArray(currentTestCase.rawData)
                  ? currentTestCase.rawData
                  : [];
                
                // Create schema structure for formatting
                const piiColumns = currentTestCase.piiColumns ?? currentTestCase.pii_columns ?? [];
                const schemaToFormat = {
                  db_name: currentTestCase.testCaseId,
                  testCaseId: currentTestCase.testCaseId,
                  columns: currentTestCase.columns,
                  piiColumns,
                  pii_columns: piiColumns,
                  messages: this.schemas[ind].messages || [], // Preserve existing messages
                  connectionId: this.schemas[ind].connectionId,
                  tables: [
                    {
                      tableName: currentTestCase.testCaseId,
                      columns: currentTestCase.columns,
                      rawData: formattedRawData,
                      piiColumns,
                      pii_columns: piiColumns,
                      primaryKey: currentTestCase.primaryKey,
                      foreignKey: currentTestCase.foreignKey || []
                    }
                  ]
                };
                
                // Format the schema using the service
                let formatted = this.tdgService.getFormatedResponse([schemaToFormat]);
                
                // Preserve messages after formatting
                formatted[0].messages = this.schemas[ind].messages || [];
                
                // Update the current schema
                this.schemas[ind] = formatted[0];
                
                this.tdgService.setRetrievedSchemas(this.schemas);
                this.switchTab(this.schemas[ind]);
              }
            }
          } else {
            // Fallback to old response structure
            let ind = this.schemas.findIndex(
              (ele: any) => ele.connectionId === this.selectedSchema.connectionId
            );
            let formatted = this.tdgService.getFormatedResponse(res.result);
            this.schemas[ind].tables = formatted[0]?.tables;
            this.tdgService.setRetrievedSchemas(this.schemas);
            this.switchTab(
              this.schemas[
                this.schemas.findIndex(
                  (ele: any) =>
                    this.selectedSchema.connectionId === ele.connectionId
                )
              ]
            );
          }
        }
      });
  }

  updateSchemasInStore() {
    this.tdgService.setRetrievedSchemas(this.schemas);
  }

  openValidationError() {
    let modalRef = this.modalService.open(ValidationWarningModalComponent, {
        centered: true,
        backdrop: "static",
        size: "lg",
    });
  
   
    modalRef.componentInstance.configs = {
        title: this.isFileValidation ? "File Validation Failed" : "Compatibility Issues Detected",
        subTitle: this.isFileValidation ? this.errorData.length + " file(s) failed validation" : this.errorData.length + " test case(s) failed compatibility validation",
        requiredAction: this.isFileValidation ? " Fix the following validation errors and re-upload your files to continue.": "Review and resolve the following compatibility issues before continuing with test data generation. ",
        tip: this.isFileValidation ? "Click on the file name to expand and view detailed instructions." :"Expand each test case to view detailed recommendations",
        data: this.errorData,
        buttonText: "OK",
    };
  }

  deleteTable(tab:any){
    this.tableToDelete = tab;
    let modalRef = this.modalService.open(DeleteConfirmationComponent, {
      windowClass:"delete-confirmation-window",
      centered: true,
      size: "sm",
      backdrop: "static",
    });

    modalRef.result.then((result: any) => {
      if (result) {
        // console.log("Delete-->>", this.tableToDelete);
        this.schemas = this.schemas.map((tb:any) =>
          tb.testCaseId === this.tableToDelete?.testCaseId
            ? { ...tb, isLoading: true }
            : tb
        );
        
        let payload: any = {
          testsuite:this.selectedTestSuiteData?.testSuite,
          parse_file_id : this.parseFileId,
          testcaseid : this.tableToDelete?.testCaseId
        };
        this.restService.deleteTestData(payload).subscribe(
          (response: any) => {
            if(response?.custom_table_deleted){
              this.toaster.pop({ type: 'success', text: MessageEnum.SUCCESS_DELETE_TABLE });
              this.schemas = this.schemas.filter((tb:any) => tb.testCaseId !== this.tableToDelete?.testCaseId);
              if(this.schemas?.length >0){
                this.selectedSchema= this.schemas[0]
              }else{
                this.selectedSchema =[];
              }
            }
          },
          (error) => {
            console.error(error);
            this.toaster.pop({ type: 'error', text: MessageEnum.FAILURE_DELETE_TABLE });
            this.schemas = this.schemas.map((tb:any) =>
              tb.testCaseId === this.tableToDelete?.testCaseId
                ? { ...tb, isLoading: false }
                : tb
            );
          });
        }
    });
  }

  saveAndProceed() {
    if (!this.parseFileId || !this.projectId) {
      this.toaster.pop({
        text: MessageEnum.DATA_MISSING,
        type: 'error'
      });
      return;
    }

    this.isLoading = true;
    this.restService.syncCustomTables(this.parseFileId, this.projectId).subscribe(
      (response: any) => {
        this.isLoading = false;
        if (response.success) {
          this.toaster.pop({
            text: MessageEnum.TABLE_SYNC_SUCCESS,
            type: 'success'
          });
          this.showAiSupport = false;
          this.backToVersionHistory.emit();
        } else {
          this.toaster.pop({
            text: MessageEnum.TABLE_SYNC_FAILURE,
            type: 'error'
          });
        }
      },
      (error) => {
        console.error('Error syncing custom tables:', error);
        this.isLoading = false;
        this.toaster.pop({
          text: MessageEnum.SYNCING_CUSTOM_TABLE_ERROR,
          type: 'error'
        });
      }
    );
  }

}
